import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, getDocs, query, where } from 'firebase/firestore';
import { toast } from 'react-hot-toast';
import { useStudents } from '../lib/useStudents';
import { useSchoolId } from '../lib/auth/schoolContext';
import { suggestInterventionTargets } from '../lib/functions';
import { getStaffRoleLabel } from '../lib/staff';
import StaffLeadSelect from './StaffLeadSelect';
import { MAX_GROUP_SIZE, MAX_GROUPS_PER_STUDENT, groupSizeError } from '../lib/interventionLimits';

const InterventionForm = ({ onInterventionCreated }) => {
  const { students, loading: studentsLoading } = useStudents({ orderByLastName: true, ensureSubscription: true });
  const { schoolId } = useSchoolId();
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [interventionData, setInterventionData] = useState({
    name: '',
    type: '',
    description: '',
    startDate: '',
    endDate: '',
    frequency: 'weekly',
    duration: '',
    weeks: '',
    targetAreas: [],
    // Up to 3 specific measurable targets for the intervention
    targets: [
      { id: 1, text: '' },
      { id: 2, text: '' },
      { id: 3, text: '' },
    ],
    notes: '',
    schoolId: schoolId
  });
  const [expandedYearGroups, setExpandedYearGroups] = useState(new Set());
  const [staff, setStaff] = useState([]);
  const [staffLoading, setStaffLoading] = useState(true);
  const [leadStaffId, setLeadStaffId] = useState('');
  const [suggestingTargets, setSuggestingTargets] = useState(false);
  const [targetRationales, setTargetRationales] = useState({});

  useEffect(() => {
    if (!schoolId) return;
    setInterventionData(prev => ({ ...prev, schoolId }));
  }, [schoolId]);

  const interventionTypes = [
    'Speech and Language Therapy',
    'Occupational Therapy',
    'Physical Therapy',
    'Social Skills Group',
    'Reading Intervention',
    'Math Intervention',
    'Behavioral Support',
    'Sensory Integration',
    'Other'
  ];

  const targetAreas = [
    'Communication',
    'Social Skills',
    'Academic Skills',
    'Motor Skills',
    'Behavior',
    'Sensory Processing',
    'Emotional Regulation',
    'Independence Skills'
  ];

  useEffect(() => {
    // clear selection if some selected students no longer exist
    if (selectedStudents.length && students.length) {
      setSelectedStudents(prev => prev.filter(id => students.find(s => s.id === id)));
    }
  }, [students, selectedStudents.length]);

  // Fetch active staff for lead selection
  useEffect(() => {
    const fetchStaff = async () => {
      try {
        setStaffLoading(true);
        const qStaff = query(collection(db, 'staff'), where('schoolId', '==', schoolId));
        const snap = await getDocs(qStaff);
        const staffData = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(s => s.active !== false);
        // Sort by display name on the client side
        staffData.sort((a, b) => {
          const aName = a.name || `${a.firstName || ''} ${a.lastName || ''}`.trim();
          const bName = b.name || `${b.firstName || ''} ${b.lastName || ''}`.trim();
          return aName.localeCompare(bName);
        });
        setStaff(staffData);
      } catch (e) {
        console.error('Failed to load staff', e);
        toast.error('Failed to load staff directory');
      } finally {
        setStaffLoading(false);
      }
    };
    if (schoolId) fetchStaff();
  }, [schoolId]);

  // Active assignments per student — refreshed when the staff list loads so
  // we can block selection of students already at the 3-active-interventions
  // cap. Map of studentId → count of active assignments.
  const [activeAssignmentCounts, setActiveAssignmentCounts] = useState({});

  useEffect(() => {
    const fetchAssignmentCounts = async () => {
      if (!schoolId) return;
      try {
        const qAss = query(
          collection(db, 'interventionAssignments'),
          where('schoolId', '==', schoolId),
          where('status', '==', 'active'),
        );
        const snap = await getDocs(qAss);
        const counts = {};
        snap.docs.forEach((d) => {
          const sid = d.data().studentId;
          if (sid) counts[sid] = (counts[sid] || 0) + 1;
        });
        setActiveAssignmentCounts(counts);
      } catch (e) {
        // Silent: dashboard still works, just no cap warning.
        console.warn('Could not load active assignment counts', e);
      }
    };
    fetchAssignmentCounts();
  }, [schoolId]);

  const handleStudentSelection = (studentId) => {
    const isCurrentlySelected = selectedStudents.includes(studentId);
    if (!isCurrentlySelected) {
      if (selectedStudents.length >= MAX_GROUP_SIZE) {
        toast.error(groupSizeError(MAX_GROUP_SIZE + 1));
        return;
      }
      const currentCount = activeAssignmentCounts[studentId] || 0;
      if (currentCount >= MAX_GROUPS_PER_STUDENT) {
        toast.error(`This student is already in ${MAX_GROUPS_PER_STUDENT} active interventions (cap reached).`);
        return;
      }
    }
    setSelectedStudents(prev =>
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSuggestTargets = async () => {
    if (!interventionData.name || !interventionData.type) {
      toast.error('Please fill in name and type before asking AI for targets.');
      return;
    }
    if (selectedStudents.length === 0) {
      toast.error('Pick at least one student so AI knows the cohort\u2019s working level.');
      return;
    }
    setSuggestingTargets(true);
    try {
      const result = await suggestInterventionTargets({
        intervention: {
          name: interventionData.name,
          interventionType: interventionData.type,
          type: interventionData.type,
          description: interventionData.description || '',
          targetedNeeds: interventionData.targetAreas || [],
          frequency: interventionData.frequency || '',
          duration: interventionData.duration || '',
        },
        studentIds: selectedStudents,
      });
      const suggested = (result?.data?.targets || []).slice(0, 3);
      if (suggested.length === 0) {
        toast.error('AI returned no targets. Try again.');
        return;
      }
      const padded = [0, 1, 2].map((i) => ({
        id: i + 1,
        text: suggested[i]?.text || '',
      }));
      const rationales = {};
      suggested.forEach((t, i) => {
        if (t.rationale) rationales[i + 1] = t.rationale;
      });
      setInterventionData((prev) => ({ ...prev, targets: padded }));
      setTargetRationales(rationales);
      toast.success(`AI generated ${suggested.length} SMART targets.`);
    } catch (error) {
      console.error('Failed to suggest targets', error);
      toast.error(error?.message || 'Could not generate targets.');
    } finally {
      setSuggestingTargets(false);
    }
  };

  const handleTargetAreaToggle = (area) => {
    setInterventionData(prev => ({
      ...prev,
      targetAreas: prev.targetAreas.includes(area)
        ? prev.targetAreas.filter(a => a !== area)
        : [...prev.targetAreas, area]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedStudents.length === 0) {
      toast.error('Please select at least one student');
      return;
    }

    const sizeErr = groupSizeError(selectedStudents.length);
    if (sizeErr) {
      toast.error(sizeErr);
      return;
    }

    if (!interventionData.name || !interventionData.type) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!schoolId) {
      toast.error('School context is not ready yet. Please try again in a moment.');
      return;
    }

    if (!leadStaffId) {
      toast.error('Please select a lead staff member');
      return;
    }

    try {
      // Clean targets: keep up to 3, non-empty text only
      const cleanedTargets = (interventionData.targets || [])
        .slice(0, 3)
        .map((t, idx) => ({ id: idx + 1, text: (t?.text || '').trim() }))
        .filter(t => t.text.length > 0);

      const leadStaff = staff.find(s => s.id === leadStaffId) || null;

      // Build a session schedule based on startDate + weeks (weekly/biweekly), or start-end range
      const sessionSchedule: string[] = [];
      const start = interventionData.startDate ? new Date(interventionData.startDate) : null;
      const weeksNum = Number(interventionData.weeks || 0);
      if (start && weeksNum > 0) {
        const stepDays = interventionData.frequency === 'biweekly' ? 14 : 7;
        for (let i = 0; i < weeksNum; i++) {
          const d = new Date(start);
          d.setDate(start.getDate() + i * stepDays);
          sessionSchedule.push(d.toISOString().split('T')[0]);
        }
      } else if (start && interventionData.endDate) {
        const end = new Date(interventionData.endDate);
        const stepDays = interventionData.frequency === 'biweekly' ? 14 : (interventionData.frequency === 'daily' ? 1 : 7);
        const d = new Date(start);
        while (d <= end) {
          sessionSchedule.push(d.toISOString().split('T')[0]);
          d.setDate(d.getDate() + stepDays);
        }
      }

      const interventionDoc = await addDoc(collection(db, 'interventions'), {
        ...interventionData,
        targets: cleanedTargets,
        sessionSchedule,
        students: selectedStudents,
        leadStaffId,
        leadStaffName: leadStaff?.name || `${leadStaff?.firstName || ''} ${leadStaff?.lastName || ''}`.trim(),
        leadStaffRole: getStaffRoleLabel(leadStaff?.role),
        leadStaffEmail: leadStaff?.email || '',
        createdAt: new Date(),
        status: 'active',
        createdBy: 'school-user',
        sessions: []
      });

      // Helper to infer sessions/week and minutes/session from form inputs
      const frequencyToSessions = (freq) => {
        const f = String(freq || '').toLowerCase();
        if (f === 'daily') return 5;
        if (f === 'weekly') return 1;
        if (f === 'biweekly') return 0.5;
        if (f === 'monthly') return 0.25;
        return 1;
      };
      const sessionsFromSchedule = (schedule) => {
        if (!Array.isArray(schedule) || schedule.length === 0) return null;
        const ds = schedule
          .map((s) => { const d = new Date(s); return isNaN(d.getTime()) ? null : d; })
          .filter(Boolean)
          .sort((a,b) => a.getTime() - b.getTime());
        if (ds.length === 0) return null;
        const first = ds[0].getTime();
        const last = ds[ds.length - 1].getTime();
        const days = Math.max(1, Math.round((last - first) / (1000*60*60*24)) + 1);
        const weeksSpan = Math.max(1, Math.round(days / 7));
        return schedule.length / weeksSpan;
      };
      const parseDurationToMinutes = (duration) => {
        if (!duration) return 30;
        const s = String(duration).toLowerCase();
        const hMatch = s.match(/(\d+)\s*h/);
        const mMatch = s.match(/(\d+)\s*m/);
        if (hMatch || mMatch) {
          const hours = hMatch ? parseInt(hMatch[1], 10) : 0;
          const mins = mMatch ? parseInt(mMatch[1], 10) : 0;
          const total = (isNaN(hours) ? 0 : hours) * 60 + (isNaN(mins) ? 0 : mins);
          return total || 30;
        }
        const firstNum = s.match(/(\d+)/);
        return firstNum ? parseInt(firstNum[1], 10) : 30;
      };

      // Create intervention assignments for each student with defaults from intervention
      const defaultSessions = (sessionsFromSchedule(sessionSchedule) || frequencyToSessions(interventionData.frequency));
      const defaultMinutes = parseDurationToMinutes(interventionData.duration);
      const assignmentPromises = selectedStudents.map(studentId =>
        addDoc(collection(db, 'interventionAssignments'), {
          interventionId: interventionDoc.id,
          studentId,
          assignedDate: new Date(),
          status: 'active',
          staffId: leadStaffId || null,
          sessionsPerWeek: defaultSessions,
          minutesPerSession: defaultMinutes,
          schoolId,
        })
      );

      await Promise.all(assignmentPromises);

      toast.success('Intervention created successfully!');
      setInterventionData({
        name: '',
        type: '',
        description: '',
        startDate: '',
        endDate: '',
        frequency: 'weekly',
        duration: '',
        weeks: '',
        targetAreas: [],
        targets: [
          { id: 1, text: '' },
          { id: 2, text: '' },
          { id: 3, text: '' },
        ],
        notes: '',
        schoolId
      });
      setSelectedStudents([]);
      setLeadStaffId('');
      setTargetRationales({});
      
      if (onInterventionCreated) {
        onInterventionCreated();
      }
    } catch (error) {
      console.error('Error creating intervention:', error);
      toast.error('Failed to create intervention');
    }
  };

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-6">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">Create New Intervention</h2>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Intervention Name *
            </label>
            <input
              type="text"
              value={interventionData.name}
              onChange={(e) => setInterventionData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Reading Support Group"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Intervention Type *
            </label>
            <select
              value={interventionData.type}
              onChange={(e) => setInterventionData(prev => ({ ...prev, type: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Select type</option>
              {interventionTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            value={interventionData.description}
            onChange={(e) => setInterventionData(prev => ({ ...prev, description: e.target.value }))}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Describe the intervention goals and approach..."
          />
        </div>

        {/* Dates and Frequency */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={interventionData.startDate}
              onChange={(e) => setInterventionData(prev => ({ ...prev, startDate: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              value={interventionData.endDate}
              onChange={(e) => setInterventionData(prev => ({ ...prev, endDate: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Frequency
            </label>
            <select
              value={interventionData.frequency}
              onChange={(e) => setInterventionData(prev => ({ ...prev, frequency: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="biweekly">Bi-weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Weeks (optional)
            </label>
            <input
              type="number"
              min="1"
              value={interventionData.weeks}
              onChange={(e) => setInterventionData(prev => ({ ...prev, weeks: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., 6"
            />
            <p className="text-xs text-gray-500 mt-1">Used to auto-generate weekly/biweekly session dates.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Session Duration
            </label>
            <input
              type="text"
              value={interventionData.duration}
              onChange={(e) => setInterventionData(prev => ({ ...prev, duration: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., 30 minutes"
            />
          </div>
        </div>

        {/* Target Areas */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Target Areas
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {targetAreas.map(area => (
              <label key={area} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={interventionData.targetAreas.includes(area)}
                  onChange={() => handleTargetAreaToggle(area)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">{area}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Intervention Targets (up to 3) */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Intervention Targets (up to 3)
            </label>
            <button
              type="button"
              onClick={handleSuggestTargets}
              disabled={suggestingTargets || !interventionData.name || !interventionData.type || selectedStudents.length === 0}
              className={`text-xs px-3 py-1.5 rounded-md font-medium transition-colors ${
                suggestingTargets || !interventionData.name || !interventionData.type || selectedStudents.length === 0
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-purple-600 text-white hover:bg-purple-700'
              }`}
              title={!interventionData.name || !interventionData.type
                ? 'Fill in name + type first'
                : selectedStudents.length === 0
                  ? 'Select at least one student first'
                  : 'Generate SMART targets tailored to this cohort'}
            >
              {suggestingTargets ? 'Generating…' : '✨ Suggest SMART targets with AI'}
            </button>
          </div>
          <p className="text-xs text-gray-500 mb-2">Clear, measurable targets help session leaders track progress each session. AI tailors targets to the cohort's working level.</p>
          <div className="space-y-2">
            {(interventionData.targets || []).slice(0, 3).map((t, idx) => (
              <div key={idx} className="space-y-1">
                <div className="flex items-start gap-2">
                  <span className="mt-2 text-xs text-gray-500 w-5">{idx + 1}.</span>
                  <input
                    type="text"
                    value={t.text}
                    onChange={(e) => setInterventionData(prev => {
                      const next = [...(prev.targets || [])];
                      next[idx] = { id: idx + 1, text: e.target.value };
                      return { ...prev, targets: next };
                    })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={idx === 0 ? 'e.g., Read 10 CVC words with 90% accuracy by end of half-term' : 'Enter a SMART target'}
                  />
                </div>
                {targetRationales[idx + 1] && (
                  <p className="text-xs text-purple-700 italic pl-7">AI rationale: {targetRationales[idx + 1]}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Lead Staff */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Lead Staff Member *
          </label>
          <StaffLeadSelect
            staff={staff}
            value={leadStaffId}
            onChange={(e) => setLeadStaffId(e.target.value)}
            loading={staffLoading}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Select lead staff"
            emptyLabel="No active staff. Add SLT, OT, EP or Physio in Staff Directory"
          />
        </div>

        {/* Student Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Students *
          </label>
          <div className="max-h-96 overflow-y-auto border border-gray-300 rounded-md">
            {studentsLoading ? (
              <div className="p-4 text-gray-500">Loading students…</div>
            ) : students.length === 0 ? (
              <div className="p-4 text-gray-500">No students available</div>
            ) : (
              <div className="divide-y">
                {Object.entries(
                  students.reduce((acc, s) => {
                    const yg = s.yearGroup || 'Unknown';
                    (acc[yg] ||= []).push(s);
                    return acc;
                  }, {})
                )
                  .sort(([a], [b]) => {
                    const order = {
                      'Reception': 0,
                      'Year 1': 1,
                      'Year 2': 2,
                      'Year 3': 3,
                      'Year 4': 4,
                      'Year 5': 5,
                      'Year 6': 6,
                      'Year 7': 7,
                      'Year 8': 8,
                      'Year 9': 9,
                      'Year 10': 10,
                      'Year 11': 11,
                    };
                    const ao = a in order ? order[a] : 999;
                    const bo = b in order ? order[b] : 999;
                    if (ao !== bo) return ao - bo;
                    return String(a).localeCompare(String(b));
                  })
                  .map(([yearGroup, list]) => (
                    <div key={yearGroup}>
                      <button
                        type="button"
                        onClick={() => {
                          const next = new Set(expandedYearGroups);
                          if (next.has(yearGroup)) next.delete(yearGroup); else next.add(yearGroup);
                          setExpandedYearGroups(next);
                        }}
                        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100"
                      >
                        <div className="flex items-center gap-3">
                          <span className="font-medium text-gray-900">{yearGroup}</span>
                          <span className="text-sm text-gray-500">{list.length} students</span>
                        </div>
                        <svg className={`w-5 h-5 text-gray-500 transition-transform ${expandedYearGroups.has(yearGroup) ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {expandedYearGroups.has(yearGroup) && (
                        <div className="px-4 py-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                          {list
                            .slice()
                            .sort((a, b) => a.lastName.localeCompare(b.lastName))
                            .map((student) => {
                              const activeCount = activeAssignmentCounts[student.id] || 0;
                              const atStudentCap = activeCount >= MAX_GROUPS_PER_STUDENT && !selectedStudents.includes(student.id);
                              const atGroupCap = selectedStudents.length >= MAX_GROUP_SIZE && !selectedStudents.includes(student.id);
                              const disabled = atStudentCap || atGroupCap;
                              return (
                                <label
                                  key={student.id}
                                  className={`flex items-center gap-2 p-2 rounded ${disabled ? 'opacity-60 cursor-not-allowed' : 'hover:bg-gray-50'}`}
                                  title={
                                    atGroupCap
                                      ? `This group already has ${MAX_GROUP_SIZE} students.`
                                      : atStudentCap
                                        ? `Student is already in ${MAX_GROUPS_PER_STUDENT} active interventions.`
                                        : ''
                                  }
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedStudents.includes(student.id)}
                                    onChange={() => handleStudentSelection(student.id)}
                                    disabled={disabled}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed"
                                  />
                                  <span className="text-sm text-gray-700 flex-1">
                                    {student.firstName} {student.lastName}
                                    <span className="text-gray-400"> • {student.phase || 'Phase'}{student.academicLevel ? ` • ${student.academicLevel}` : ''}{student.workingAtLevel ? ` • working ${student.workingAtLevel}` : ''}</span>
                                  </span>
                                  {activeCount > 0 && (
                                    <span
                                      className={`text-[10px] px-1.5 py-0.5 rounded ${
                                        activeCount >= MAX_GROUPS_PER_STUDENT ? 'bg-red-100 text-red-700' : activeCount === 2 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
                                      }`}
                                    >
                                      {activeCount}/{MAX_GROUPS_PER_STUDENT}
                                    </span>
                                  )}
                                </label>
                              );
                            })}
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </div>
          {selectedStudents.length > 0 && (
            <p className={`text-sm mt-2 ${selectedStudents.length > MAX_GROUP_SIZE ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
              Selected: {selectedStudents.length} / {MAX_GROUP_SIZE} student(s)
            </p>
          )}
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Additional Notes
          </label>
          <textarea
            value={interventionData.notes}
            onChange={(e) => setInterventionData(prev => ({ ...prev, notes: e.target.value }))}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Any additional information..."
          />
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => {
              setInterventionData({
                name: '',
                type: '',
                description: '',
                startDate: '',
                endDate: '',
                frequency: 'weekly',
                duration: '',
                weeks: '',
                targetAreas: [],
                targets: [
                  { id: 1, text: '' },
                  { id: 2, text: '' },
                  { id: 3, text: '' },
                ],
                notes: '',
                schoolId
              });
              setSelectedStudents([]);
              setLeadStaffId('');
            }}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
          >
            Reset
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Create Intervention
          </button>
        </div>
      </form>
    </div>
  );
};

export default InterventionForm; 
